import React, { useEffect, useState } from "react";
import {
  getAvailablePartners,
  useAssignAwbAndPickup, // ✅ Use new combined hook
} from "../../api/orders/orders";
import { notifyError, notifySuccess } from "../../utils/ToastifyProvider";
import { useNavigate } from "react-router";

const CourierModal = ({ order, onClose, onShipped }) => {
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCourierId, setSelectedCourierId] = useState(null);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const { mutateAsync: assignAwbAndPickup } = useAssignAwbAndPickup(); // ✅ Combined API

  useEffect(() => {
    if (order && order.OrderNumber) {
      fetchCourierList();
    }
  }, [order]);

  const fetchCourierList = async () => {
    try {
      setLoading(true);
      setError(null);

      const courierPayload = {
        pickup_postcode: 382430,
        delivery_postcode: order?.BillingAddress?.Pincode,
        weight: order?.Weight,
        cod: order?.PaymentType === "cod" ? 1 : 0,
      };

      console.log("📦 Payload sent:", courierPayload);

      const courierList = await getAvailablePartners(courierPayload);

      console.log("🚚 Raw API Response:", courierList);

      setCouriers(courierList);
    } catch (err) {
      console.error("❌ Fetch courier error:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShip = async (courier) => {
    try {
      setSelectedCourierId(courier?.courier_company_id);

      const shipment_id = order?.shipment_id;

      // ✅ Call combined API (AWB + Pickup in one request)
      const res = await assignAwbAndPickup({
        shipment_id,
        courier_id: courier?.courier_company_id,
        status: "assigned",
      });

      console.log("✅ Combined API Response:", res);

      // Close modal and refresh parent component
      if (onShipped) {
        onShipped();
      } else {
        onClose();
        navigate("/orders?tab=manifest");
      }

    } catch (err) {
      console.error("❌ Ship Error:", err);
      const errorMsg = err.response?.data?.message || err.message;
      notifyError(errorMsg || "Failed to ship order");
    } finally {
      setSelectedCourierId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="relative w-full max-w-xl bg-white rounded-lg shadow-xl p-6 max-h-[85vh] flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-500 hover:text-gray-800 text-2xl"
        >
          &times;
        </button>

        <h2 className="text-xl font-bold mb-4">
          Select Courier Partner{" "}
          {!!couriers.length && (
            <span className="text-gray-500 text-sm">
              ({couriers.length} available)
            </span>
          )}
        </h2>

        {loading ? (
          <p className="text-center text-gray-500 py-6">
            Loading courier partners...
          </p>
        ) : error ? (
          <p className="text-center text-red-500 py-6">{error}</p>
        ) : couriers.length === 0 ? (
          <p className="text-center text-gray-500 py-6">
            No courier partners available
          </p>
        ) : (
          <>
            <div className="grid grid-cols-4 font-semibold text-gray-600 text-sm border-b pb-2 mb-3 px-2">
              <div>Courier</div>
              <div className="text-center">Mode</div>
              <div className="text-center">ETD</div>
              <div className="text-right">Rate</div>
            </div>

            <div className="overflow-y-auto flex-1 space-y-3">
              {couriers.map((courier, i) => {
                const isLoading =
                  selectedCourierId === courier.courier_company_id;

                return (
                  <div
                    key={i}
                    className="grid grid-cols-4 items-center p-4 border rounded-lg shadow-sm hover:shadow-md transition"
                  >
                    <div>
                      <p className="font-medium text-gray-800">
                        {courier.courier_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        ID: {courier.courier_company_id}
                      </p>
                    </div>

                    <div className="text-center">
                      {courier.is_surface ? "Surface" : "Air"}
                    </div>

                    <div className="text-center">
                      {courier.etd || courier.estimated_delivery_days}
                    </div>

                    <div className="flex flex-col items-end">
                      <div className="text-lg font-bold">
                        ₹{courier.rate || courier.freight_charge}
                      </div>

                      <button
                        onClick={() => handleShip(courier)}
                        disabled={isLoading}
                        className="mt-2 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? "Shipping..." : "Ship"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CourierModal;